import {
  APPOINTMENT_REMOTE_ID,
  AppointmentSpeciality,
} from '@avanta/shared/constants/appointments';
import { FIFTEEN_MINUTES } from '@avanta/shared/constants/common';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentService } from 'services/appointments';
import { UsersService } from 'services/users';
import { type RecentPsychologist } from 'types/appointments';
import { getUserId } from 'utils/storage';

async function fetchAndUnifyPsychologists(patientId: string): Promise<RecentPsychologist[]> {
  const [resAppointments, resTherapists] = await Promise.all([
    AppointmentService.getRecentPsychologists(patientId),
    UsersService.getAllRecentPsychologists(),
  ]);

  const unifiedMap = new Map(
    resAppointments.data.map(item => [
      item.psychologist.id,
      {
        psychologist: item.psychologist,
        specialitiesBookable: item.specialitiesBookable,
        centerId: item.centerId,
      },
    ])
  );

  return Array.from(
    resTherapists.data
      .reduce((map, psycho) => {
        const existing = map.get(psycho.id);
        const newPsychologist = existing
          ? { ...existing, psychologist: { ...existing.psychologist, ...psycho } }
          : {
              psychologist: psycho,
              specialitiesBookable: Number(AppointmentSpeciality.ADULT_PSYCHOLOGY),
              centerId: APPOINTMENT_REMOTE_ID,
            };
        return map.set(psycho.id, newPsychologist);
      }, unifiedMap)
      .values()
  );
}

export const useFetchRecentPsychologists = (): UseQueryResult<RecentPsychologist[]> => {
  const patientId = getUserId();
  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_RECENT_PSYCHOLOGISTS, patientId],
    queryFn: async () => {
      const recentPsychologists = await fetchAndUnifyPsychologists(patientId);
      const response = recentPsychologists.map(async recentPsychologist => ({
        ...recentPsychologist,
        center: recentPsychologist.centerId
          ? (await AppointmentService.getCenterDetails(recentPsychologist.centerId)).data
          : undefined,
      }));
      return Promise.all(response);
    },
    enabled: !!patientId,
    staleTime: FIFTEEN_MINUTES,
  });
};
